import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowLeft, CalendarDays, Users, LayoutGrid, Sun, Search, Table2, Clock, Sparkles, Smartphone, ShieldAlert, Pause, Bell, Star, FileDown, Printer, UserX, Baby, ArrowRightLeft, StickyNote, Mail, Eye, Crown, Footprints, ListChecks, Link2, MailCheck, Upload, Tag, Ban, BookOpen, PhoneCall } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Separator } from "@/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { helpTranslations, type HelpLang } from "@/config/helpTranslations";

const Help = () => {
  const navigate = useNavigate();
  const [lang, setLang] = useState<HelpLang>("fr");
  const t = helpTranslations[lang];

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">{t.header.title}</h1>
            <p className="text-sm text-muted-foreground">{t.header.subtitle}</p>
          </div>
          <ToggleGroup
            type="single"
            value={lang}
            onValueChange={(v) => { if (v) setLang(v as HelpLang); }}
            size="sm"
          >
            <ToggleGroupItem value="fr" className="text-xs px-3">FR</ToggleGroupItem>
            <ToggleGroupItem value="en" className="text-xs px-3">EN</ToggleGroupItem>
          </ToggleGroup>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-4xl space-y-6">

        {/* Quick overview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5 text-primary" />
              {t.overview.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-4">
            <p>{t.overview.p1}</p>
            <p>{t.overview.p2}</p>
            <Separator />
            <div>
              <h4 className="font-medium text-foreground mb-1 flex items-center gap-1"><BookOpen className="h-4 w-4" /> {t.overview.onboarding.title}</h4>
              <p>{t.overview.onboarding.desc}</p>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-1 flex items-center gap-1"><PhoneCall className="h-4 w-4" /> {t.overview.trialHelp.title}</h4>
              <p>{t.overview.trialHelp.desc}</p>
            </div>
          </CardContent>
        </Card>

        <Accordion type="multiple" className="space-y-3">

          {/* 1. Réservations */}
          <AccordionItem value="reservations" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <CalendarDays className="h-5 w-5 text-primary shrink-0" />
                <span className="font-semibold">{t.reservations.title}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground space-y-4 pb-4">
              <div>
                <h4 className="font-medium text-foreground mb-1">{t.reservations.viewModes.title}</h4>
                <p>{t.reservations.viewModes.intro}</p>
                <ul className="list-disc pl-5 mt-1 space-y-1">
                  <li><strong>{t.reservations.viewModes.grouped}</strong> — {t.reservations.viewModes.groupedDesc}</li>
                  <li><strong>{t.reservations.viewModes.list}</strong> — {t.reservations.viewModes.listDesc}</li>
                  <li><strong>{t.reservations.viewModes.timeline}</strong> — {t.reservations.viewModes.timelineDesc}</li>
                </ul>
              </div>

              <Separator />

              <div>
                <h4 className="font-medium text-foreground mb-1">{t.reservations.actions.title}</h4>
                <ul className="list-disc pl-5 space-y-1">
                  <li><div className="flex items-center gap-1"><Badge variant="outline" className="text-xs">{t.reservations.actions.arrived}</Badge> — {t.reservations.actions.arrivedDesc}</div></li>
                  <li><div className="flex items-center gap-1"><Badge variant="outline" className="text-xs">{t.reservations.actions.cancel}</Badge> — {t.reservations.actions.cancelDesc}</div></li>
                  <li><div className="flex items-center gap-1"><UserX className="h-3.5 w-3.5" /> <strong>{t.reservations.actions.noShow}</strong></div> — {t.reservations.actions.noShowDesc}</li>
                  <li><strong>{t.reservations.actions.modify}</strong> — {t.reservations.actions.modifyDesc}</li>
                  <li><div className="flex items-center gap-1"><ArrowRightLeft className="h-3.5 w-3.5" /> <strong>{t.reservations.actions.quickTable}</strong></div> — {t.reservations.actions.quickTableDesc}</li>
                  <li><div className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> <strong>{t.reservations.actions.emails}</strong></div> — {t.reservations.actions.emailsDesc}</li>
                  <li><div className="flex items-center gap-1"><StickyNote className="h-3.5 w-3.5" /> <strong>{t.reservations.actions.notes}</strong></div> — {t.reservations.actions.notesDesc}</li>
                </ul>
              </div>

              <Separator />

              <div>
                <h4 className="font-medium text-foreground mb-1 flex items-center gap-1"><Footprints className="h-4 w-4" /> {t.reservations.walkIn.title}</h4>
                <p>{t.reservations.walkIn.desc}</p>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-1 flex items-center gap-1"><ListChecks className="h-4 w-4" /> {t.reservations.waitlist.title}</h4>
                <p>{t.reservations.waitlist.desc}</p>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-1 flex items-center gap-1"><Baby className="h-4 w-4" /> {t.reservations.children.title}</h4>
                <p>{t.reservations.children.desc}</p>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-1 flex items-center gap-1"><Sun className="h-4 w-4" /> {t.reservations.terracePreference.title}</h4>
                <p>{t.reservations.terracePreference.desc}</p>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-1 flex items-center gap-1"><Mail className="h-4 w-4" /> {t.reservations.selfService.title}</h4>
                <p>{t.reservations.selfService.desc}</p>
              </div>

              <Separator />

              <div>
                <h4 className="font-medium text-foreground mb-1">{t.reservations.summary.title}</h4>
                <p>{t.reservations.summary.desc}</p>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-1">{t.reservations.tools.title}</h4>
                <ul className="list-disc pl-5 space-y-1">
                  <li><div className="flex items-center gap-1"><FileDown className="h-3.5 w-3.5" /> <strong>{t.reservations.tools.csvExport}</strong></div> — {t.reservations.tools.csvExportDesc}</li>
                  <li><div className="flex items-center gap-1"><Printer className="h-3.5 w-3.5" /> <strong>{t.reservations.tools.print}</strong></div> — {t.reservations.tools.printDesc}</li>
                  <li><strong>{t.reservations.tools.search}</strong> — {t.reservations.tools.searchDesc}</li>
                </ul>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 2. Clients */}
          <AccordionItem value="clients" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-primary shrink-0" />
                <span className="font-semibold">{t.clients.title}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground space-y-4 pb-4">
              <p>{t.clients.intro}</p>
              
              <div>
                <h4 className="font-medium text-foreground mb-1">{t.clients.loyalty.title}</h4>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div className="flex items-center gap-2"><Badge variant="secondary" className="text-xs">{t.clients.loyalty.new}</Badge> <span>{t.clients.loyalty.newCount}</span></div>
                  <div className="flex items-center gap-2"><Badge variant="secondary" className="text-xs">{t.clients.loyalty.regular}</Badge> <span>{t.clients.loyalty.regularCount}</span></div>
                  <div className="flex items-center gap-2"><Badge variant="secondary" className="text-xs">{t.clients.loyalty.loyal}</Badge> <span>{t.clients.loyalty.loyalCount}</span></div>
                  <div className="flex items-center gap-2"><Badge className="text-xs bg-primary">{t.clients.loyalty.vip}</Badge> <span>{t.clients.loyalty.vipCount}</span></div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-1">{t.clients.notes.title}</h4>
                <p>{t.clients.notes.desc}</p>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-1">{t.clients.edit.title}</h4>
                <p>{t.clients.edit.desc}</p>
              </div>

              <Separator />

              <div>
                <h4 className="font-medium text-foreground mb-1 flex items-center gap-1"><Tag className="h-4 w-4" /> {t.clients.allergies.title}</h4>
                <p>{t.clients.allergies.desc}</p>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-1 flex items-center gap-1"><Ban className="h-4 w-4" /> {t.clients.statusBlacklist.title}</h4>
                <p>{t.clients.statusBlacklist.desc}</p>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-1 flex items-center gap-1"><ShieldAlert className="h-4 w-4" /> {t.clients.consent.title}</h4>
                <p>{t.clients.consent.desc}</p>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-1 flex items-center gap-1"><Upload className="h-4 w-4" /> {t.clients.importExport.title}</h4>
                <p>{t.clients.importExport.desc}</p>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 3. Plan de salle */}
          <AccordionItem value="floor-plan" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <LayoutGrid className="h-5 w-5 text-primary shrink-0" />
                <span className="font-semibold">{t.floorPlan.title}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground space-y-4 pb-4">
              <div>
                <h4 className="font-medium text-foreground mb-1">{t.floorPlan.consultation.title}</h4>
                <p>{t.floorPlan.consultation.desc}</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1">{t.floorPlan.dragDrop.title}</h4>
                <p>{t.floorPlan.dragDrop.desc}</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1">{t.floorPlan.editMode.title}</h4>
                <p>{t.floorPlan.editMode.desc}</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1">{t.floorPlan.zoom.title}</h4>
                <p>{t.floorPlan.zoom.desc}</p>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 4. Terrasse */}
          <AccordionItem value="terrace" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <Sun className="h-5 w-5 text-primary shrink-0" />
                <span className="font-semibold">{t.terrace.title}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground space-y-4 pb-4">
              <p>{t.terrace.intro}</p>
              <div>
                <h4 className="font-medium text-foreground mb-1">{t.terrace.weather.title}</h4>
                <p>{t.terrace.weather.desc}</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1">{t.terrace.closeOpen.title}</h4>
                <p>{t.terrace.closeOpen.desc}</p>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 5. Disponibilités */}
          <AccordionItem value="availability" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <Search className="h-5 w-5 text-primary shrink-0" />
                <span className="font-semibold">{t.availability.title}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground space-y-4 pb-4">
              <p>{t.availability.intro}</p>
              <div>
                <h4 className="font-medium text-foreground mb-1">{t.availability.categories.title}</h4>
                <ul className="list-disc pl-5 space-y-1">
                  <li><Badge className="text-xs" variant="default">{t.availability.categories.recommended}</Badge> — {t.availability.categories.recommendedDesc}</li>
                  <li><Badge className="text-xs" variant="secondary">{t.availability.categories.available}</Badge> — {t.availability.categories.availableDesc}</li>
                  <li><Badge className="text-xs" variant="outline">{t.availability.categories.manual}</Badge> — {t.availability.categories.manualDesc}</li>
                </ul>
              </div>
              <p><strong>{t.availability.bookCta}</strong></p>
            </AccordionContent>
          </AccordionItem>

          {/* 6. Gestion des tables */}
          <AccordionItem value="tables" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <Table2 className="h-5 w-5 text-primary shrink-0" />
                <span className="font-semibold">{t.tables.title}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground space-y-4 pb-4">
              <div>
                <h4 className="font-medium text-foreground mb-1">{t.tables.simple.title}</h4>
                <p>{t.tables.simple.intro}</p>
                <ul className="list-disc pl-5 mt-1 space-y-1">
                  <li><strong>{t.tables.simple.capacity}</strong> {t.tables.simple.capacityDesc}</li>
                  <li><strong>{t.tables.simple.bookable}</strong> — {t.tables.simple.bookableDesc}</li>
                  <li><strong>{t.tables.simple.terraceFlag}</strong> — {t.tables.simple.terraceFlagDesc}</li>
                  <li><div className="flex items-center gap-1"><Baby className="h-3.5 w-3.5" /> <strong>{t.tables.simple.children}</strong></div> — {t.tables.simple.childrenDesc}</li>
                  <li><strong>{t.tables.simple.priority}</strong> — {t.tables.simple.priorityDesc}</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1">{t.tables.combined.title}</h4>
                <p>{t.tables.combined.desc}</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1">{t.tables.blocking.title}</h4>
                <p>{t.tables.blocking.desc}</p>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 7. Créneaux horaires */}
          <AccordionItem value="time-slots" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-primary shrink-0" />
                <span className="font-semibold">{t.timeSlots.title}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground space-y-4 pb-4">
              <p>{t.timeSlots.intro}</p>
              <div>
                <h4 className="font-medium text-foreground mb-1">{t.timeSlots.config.title}</h4>
                <ul className="list-disc pl-5 space-y-1">
                  <li>{t.timeSlots.config.item1}</li>
                  <li>{t.timeSlots.config.item2}</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1">{t.timeSlots.closures.title}</h4>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong>{t.timeSlots.closures.service}</strong> — {t.timeSlots.closures.serviceDesc}</li>
                  <li><strong>{t.timeSlots.closures.establishment}</strong> — {t.timeSlots.closures.establishmentDesc}</li>
                </ul>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 8. Outils & fonctionnalités */}
          <AccordionItem value="tools" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <Sparkles className="h-5 w-5 text-primary shrink-0" />
                <span className="font-semibold">{t.toolsFeatures.title}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground space-y-4 pb-4">
              <div>
                <h4 className="font-medium text-foreground mb-1 flex items-center gap-1"><Sparkles className="h-4 w-4" /> {t.toolsFeatures.ai.title}</h4>
                <p>{t.toolsFeatures.ai.desc}</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1 flex items-center gap-1"><Smartphone className="h-4 w-4" /> {t.toolsFeatures.pwa.title}</h4>
                <p>{t.toolsFeatures.pwa.desc}</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1 flex items-center gap-1"><ShieldAlert className="h-4 w-4" /> {t.toolsFeatures.spam.title}</h4>
                <p>{t.toolsFeatures.spam.desc}</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1 flex items-center gap-1"><Pause className="h-4 w-4" /> {t.toolsFeatures.pause.title}</h4>
                <p>{t.toolsFeatures.pause.desc}</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1 flex items-center gap-1"><Bell className="h-4 w-4" /> {t.toolsFeatures.whatsNew.title}</h4>
                <p>{t.toolsFeatures.whatsNew.desc}</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1 flex items-center gap-1"><Eye className="h-4 w-4" /> {t.toolsFeatures.compactView.title}</h4>
                <p>{t.toolsFeatures.compactView.desc}</p>
              </div>

              <Separator />

              <div>
                <h4 className="font-medium text-foreground mb-1 flex items-center gap-1"><MailCheck className="h-4 w-4" /> {t.toolsFeatures.autoEmails.title}</h4>
                <p>{t.toolsFeatures.autoEmails.desc}</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1 flex items-center gap-1"><Link2 className="h-4 w-4" /> {t.toolsFeatures.reviewLinks.title}</h4>
                <p>{t.toolsFeatures.reviewLinks.desc}</p>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 9. Restrictions */}
          <AccordionItem value="restrictions" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <Crown className="h-5 w-5 text-muted-foreground shrink-0" />
                <span className="font-semibold">{t.restrictions.title}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground space-y-2 pb-4">
              <p>{t.restrictions.intro}</p>
              <ul className="list-disc pl-5 space-y-1">
                {t.restrictions.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
              <p className="mt-2">{t.restrictions.contact}</p>
            </AccordionContent>
          </AccordionItem>

        </Accordion>

        {/* Raccourcis */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t.shortcuts.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              {t.shortcuts.items.map((item, i) => (
                <div key={i} className="flex justify-between items-center py-1.5 px-3 rounded-md bg-muted/50">
                  <span className="text-muted-foreground">{item.label}</span>
                  <span className="font-medium">{item.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default Help;
